import React from "react";
import { toolbarStyle } from "@View/components/headers/HeaderMain";
import { useLocation, Link, matchPath } from "react-router-dom";
import { getRoleSafe } from "@Security/stateHelpers";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

export function findMatchedBreadcrumb(items, pathname) {
  for (const item of items) {
    const match = matchPath(pathname, { path: item.path, exact: true });
    if (match) return { item, params: match.params };
  }
  return null;
}

export const pagesNoBack = [
  "/customer/details/:id",
  "/client/details/:id",
  "/lead/:leadId",
  "/creaditOpinion/:id",
  "/client/risk/:id",
  "/findatarequest/:id/data",
  "/customer/details/:id/group/:groupId",
  "/consolidation/:customerId/request/:consolidationId",
];

const breadcrumbMapByRole = {
  admin: [
    {
      path: "/customers",
      label: (_1, _2, t) => t("Заказчики"),
    },
    {
      path: "/customer/details/:id",
      buildUrl: ({ id }, ctx) => `/customer/details/${ctx.customer?.id || id}`,
      label: (params, ctx, t) => ctx.customer?.name || null,
      parent: "/customers",
    },
    {
      path: "/customer/details/:id/group/:groupId",
      buildUrl: ({ id, groupId }, ctx) =>
        `/customer/details/${id}/group/${groupId}`,
      label: (params, ctx, t) => {
        const groupName = ctx.customerGroups?.find((i) => i.id === params.groupId)?.name;
        return groupName ? `${t("breadcrumbs.group")}: "${groupName}"` : null;
      },
      parent: "/customer/details/:id",
    },
    {
      path: "/consolidation/:customerId/request/:consolidationId",
      buildUrl: ({ customerId, consolidationId }) =>
        `/consolidation/${customerId}/request/${consolidationId}`,
      label: (params, ctx, t) => {
        const name = ctx.request?.clientName;
        return name ? `${t("breadcrumbs.consolidation")}: "${name}"` : null;
      },
      parent: (params, ctx) =>
        ctx.request?.customerId && ctx.request?.groupId
          ? `/customer/details/${ctx.request.customerId}/group/${ctx.request.groupId}`
          : null,
    },
    {
      path: "/client/details/:id",
      buildUrl: ({ id }, ctx) => `/client/details/${ctx.client?.id || id}`,
      label: (params, ctx, t) => ctx.client?.name || null,
      parent: "/customer/details/:id",
    },
    {
      path: "/lead/:leadId",
      buildUrl: ({ leadId }) => `/lead/${leadId}`,
      label: (params, ctx, t) => ctx.lead?.borrowerName || null,
      parent: "/customer/details/:id",
    },
    {
      path: "/creaditOpinion/:id",
      buildUrl: ({ id }) => `/creaditOpinion/${id}`,
      label: (params, ctx, t) => t("CreaditOpinion.btnName"),
      parent: "/client/details/:id",
    },
    {
      path: "/client/risk/:id",
      buildUrl: ({ id }) => `/client/risk/${id}`,
      label: (params, ctx, t) => t("inputs.RiskSignal"),
      parent: "/client/details/:id",
    },
    {
      path: "/findatarequest/:id/data",
      buildUrl: ({ id }) => `/findatarequest/${id}/data`,
      label: (params, ctx, t) =>
        ctx?.request?.from
          ? t("finDataRequest.title") + ` ${ctx?.request?.from}`
          : "",
      parent: "/client/details/:id",
    },
  ],
  client: [
    {
      path: "/client/details/:id",
      buildUrl: ({ id }, ctx) => `/client/details/${ctx.client?.id || id}`,
      label: (params, ctx, t) => ctx.client?.name || null,
    },
    {
      path: "/findatarequest/:id/data",
      buildUrl: ({ id }) => `/findatarequest/${id}/data`,
      label: (params, ctx, t) =>
        ctx?.request?.from
          ? t("finDataRequest.title") + ` ${ctx?.request?.from}`
          : "",
      parent: "/client/details/:id",
    },
    {
      path: "/client/risk/:id",
      buildUrl: ({ id }) => `/client/risk/${id}`,
      label: (params, ctx, t) => t("inputs.RiskSignal"),
      parent: "/client/details/:id",
    },
  ],
  customer: [
    {
      path: "/customer/details/:id",
      buildUrl: ({ id }, ctx) => `/customer/details/${ctx.customer?.id || id}`,
      label: (params, ctx, t) => ctx.customer?.name || null,
    },
    {
      path: "/customer/details/:id/group/:groupId",
      buildUrl: ({ id, groupId }, ctx) =>
        `/customer/details/${id}/group/${groupId}`,
      label: (params, ctx, t) => {
        const groupName = ctx.customerGroups?.find((i) => i.id === params.groupId)?.name;
        return groupName ? `${t("breadcrumbs.group")}: "${groupName}"` : null;
      },
      parent: "/customer/details/:id",
    },
    {
      path: "/consolidation/:customerId/request/:consolidationId",
      buildUrl: ({ customerId, consolidationId }) =>
        `/consolidation/${customerId}/request/${consolidationId}`,
      label: (params, ctx, t) => {
        const name = ctx.request?.clientName;
        return name ? `${t("breadcrumbs.consolidation")}: "${name}"` : null;
      },
      parent: (params, ctx) =>
        ctx.request?.customerId && ctx.request?.groupId
          ? `/customer/details/${ctx.request.customerId}/group/${ctx.request.groupId}`
          : null,
    },
    {
      path: "/client/details/:id",
      buildUrl: ({ id }, ctx) => `/client/details/${ctx.client?.id || id}`,
      label: (params, ctx, t) => ctx.client?.name || null,
      parent: "/customer/details/:id",
    },
    {
      path: "/lead/:leadId",
      buildUrl: ({ leadId }) => `/lead/${leadId}`,
      label: (params, ctx, t) => ctx.lead?.borrowerName || null,
      parent: "/customer/details/:id",
    },
    {
      path: "/findatarequest/:id/data",
      buildUrl: ({ id }) => `/findatarequest/${id}/data`,
      label: (params, ctx, t) =>
        ctx?.request?.from
          ? t("finDataRequest.title") + ` ${ctx?.request?.from}`
          : "",
      parent: "/client/details/:id",
    },
    {
      path: "/creaditOpinion/:id",
      buildUrl: ({ id }) => `/creaditOpinion/${id}`,
      label: (params, ctx, t) => t("CreaditOpinion.btnName"),
      parent: "/client/details/:id",
    },
    {
      path: "/client/risk/:id",
      buildUrl: ({ id }) => `/client/risk/${id}`,
      label: (params, ctx, t) => t("inputs.RiskSignal"),
      parent: "/client/details/:id",
    },
  ],
};

const CrumbsItems = () => {
  const { t } = useTranslation();
  const client = useSelector((state) => state.client.client);
  const customer = useSelector((state) => state.customer.customer);
  const finDataRequest = useSelector((state) => state.request.request);
  const customerGroups = useSelector((state) => state.customer?.groups);
  const leadState = useSelector((state) => state.lead?.lead);

  const clientData = client?.id
    ? { id: client.id, name: client.name }
    : finDataRequest?.clientId
      ? { id: finDataRequest.clientId, name: finDataRequest.clientName }
      : {};
  const customerData = customer?.id
    ? { id: customer.id, name: customer.name }
    : client?.customerId && client?.customerName
      ? { id: client.customerId, name: client.customerName }
      : finDataRequest?.customerId && finDataRequest?.customerName
        ? { id: finDataRequest.customerId, name: finDataRequest.customerName }
        : {};
  // For consolidation, check clientName instead of id (FinancialStatementsModel doesn't have id)
  const hasRequestData = finDataRequest?.id || finDataRequest?.clientName;
  const finDataRequestData = hasRequestData
    ? {
      id: finDataRequest.id,
      from: finDataRequest.formatedStartDate,
      clientName: finDataRequest.clientName,
      groupId: finDataRequest.groupId,
      groupName: finDataRequest.groupName,
      customerId: finDataRequest.customerId,
      customerName: finDataRequest.customerName,
    }
    : {};

  // For consolidation breadcrumbs, enrich customerGroups with group info from request if available
  const enrichedCustomerGroups = [...(customerGroups || [])];
  if (finDataRequest?.groupId && finDataRequest?.groupName &&
    !enrichedCustomerGroups.find(g => g.id === finDataRequest.groupId)) {
    enrichedCustomerGroups.push({ id: finDataRequest.groupId, name: finDataRequest.groupName });
  }

  const leadData = leadState?.id
    ? { id: leadState.id, borrowerName: leadState.borrowerName, customerId: leadState.leadGeneratorCustomerId }
    : {};

  const pageData = {
    client: clientData,
    customer: customerData,
    request: finDataRequestData,
    customerGroups: enrichedCustomerGroups,
    lead: leadData,
  };

  const buildTrail = (path, items) => {
    const match = findMatchedBreadcrumb(items, path);
    if (!match) return [];

    const { item, params } = match;
    const label =
      typeof item.label === "function"
        ? item.label(params, pageData, t)
        : item.label;
    const url =
      typeof item.buildUrl === "function"
        ? item.buildUrl(params, pageData)
        : item.path;

    // Support parent as function for dynamic parent paths (e.g., consolidation)
    const parentPath =
      typeof item.parent === "function"
        ? item.parent(params, pageData)
        : item.parent;
    const parentTrail = parentPath ? buildTrail(parentPath, items) : [];

    // Skip items with null/empty label (data not loaded yet)
    if (!label) return parentTrail;

    return [...parentTrail, { label, url, path: item.path }];
  };

  const location = useLocation();
  const currentPath = location.pathname;

  const userRole = getRoleSafe() || "";
  const breadcrumbMapByRoleData =
    userRole === "Admin"
      ? breadcrumbMapByRole.admin
      : userRole.startsWith("Client")
        ? breadcrumbMapByRole.client
        : breadcrumbMapByRole.customer;

  const trail = buildTrail(currentPath, breadcrumbMapByRoleData);

  return (
    <div style={toolbarStyle}>
      <div className="crumbs">
        {trail.map((item, index) => (
          <React.Fragment key={item.path}>
            <Link to={item.url}>{item.label}</Link>
            {index < trail.length - 1 && <span>→</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default CrumbsItems;
